import { defaultShouldDehydrateQuery, isServer, QueryClient } from "@tanstack/react-query";

/**
 * Create a query client to be used on the server and browser.
 *
 * @returns A query client.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 1000, // 5 seconds
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) || query.state.status === "pending",
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

/**
 * Get a query client.
 *
 * On the server, we create a new query client for each request.
 * On the browser, we reuse the same query client across suspense boundaries.
 *
 * Note: Next.js uses implicit suspense boundaries so we need to be careful to
 * avoid creating too many query clients and loose the benefits of query persistence.
 *
 * @returns A query client.
 */
export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
